/**
 * siteOrderLink.js — A website order link, read into a Real Estate quote.
 *
 * Paste the share link of an order built on the website's order page
 * (rotoflight.com/real-estate/order?...) and the RotoGears builder fills the
 * quote's size and services on the New list. Link paste only (Cyprian,
 * 2026-10-03): the website knows nothing about this, so nothing on it changed.
 *
 * MIRRORS, in the rotoflight-media repo (the two repos deploy separately, so
 * nothing is imported from there; change this file when those change):
 * - src/utils/urlStateV2.js        readConfigFromURLV2 / decodeSelection: the
 *                                  link format (param letters, v: f: q: x: parts)
 * - src/data/serviceCatalogV2.js   service, tier, option and product ids; what
 *                                  a tier includes, refuses or stands in for;
 *                                  what an option needs; how several reel tiers
 *                                  share options (selectionLines, lineFeatureIds)
 * - src/data/addOnsV2.js           add-on ids and the gates that decide whether
 *                                  the website charges one
 * - src/hooks/useConfiguratorV2.js availableAddOns, and the rules the order
 *                                  page applies to a restored link (OrderV2's
 *                                  hydration: a land order is land only, a
 *                                  Simple Reel needs a shoot, an option goes
 *                                  when what it needs goes; its size gate)
 * - src/components/v2/BuildPathwayV2.jsx  a vertical RotoTour with no tier
 *                                  lands on the first one (flyNeedsTier)
 * Only ids and rules are copied, never prices: every price comes from the New
 * list (SVC_DB_NEW in data/serviceCatalog.js). An id this file doesn't know is
 * listed back to the user rather than dropped, so drift shows up on the first
 * link that carries it.
 */
import {
  rgFindProduct,
  rgFindAddon,
  rgGetPrice,
  rgAddonPrice,
  rgPriceContext,
  rgBillableAddonIds,
  rgIsTiered,
  rgTierIndex,
  RG_CURRENT_VERSION,
  SVC_DB_NEW,
} from '../data/serviceCatalog.js';

// ── The website's order catalog: structure only ─────────────────────────────

// Per service: its option ids, its tiers (what each includes, refuses, or is a
// stand-in for), what an option needs, and which delivery formats take it.
const SITE_SERVICES = {
  photo: {
    label: 'Photos',
    features: ['aerial', 'ig_carousel', 'twilight'],
    variants: {
      flash_photos: {},
      // The tier IS the aerials (providesFeatures).
      aerial_photos: { provides: ['aerial'] },
    },
  },
  film: {
    label: 'Listing Video',
    features: ['fpv', 'teaser_reel', 'speed_ramps', 'agent_on_camera', 'timelapse'],
    variants: {
      listing_film: {},
      estate_film: { includes: ['fpv', 'teaser_reel', 'speed_ramps', 'agent_on_camera', 'timelapse'] },
    },
  },
  flythrough: {
    label: 'RotoTour',
    features: ['teaser_reel'],
    formats: { teaser_reel: ['horizontal', 'both'] },
    variants: { paced_tour: {}, story_tour: {} },
  },
  reel: {
    label: 'Reel',
    // Several tiers at once, each with a count (x:), sharing the chosen options.
    multiLine: true,
    features: ['agent_on_camera', 'custom_captions', 'timelapse', 'speed_ramps'],
    requires: { custom_captions: 'agent_on_camera' },
    variants: {
      dynamic_reel: { includes: ['speed_ramps'] },
      story_reel: { includes: ['agent_on_camera'], excludes: ['custom_captions', 'speed_ramps'] },
    },
  },
};

const SITE_FORMATS = ['horizontal', 'vertical', 'both'];
// verticalTourVariantId: the RotoTour base is horizontal only, so a vertical
// one starts on the first tier.
const VERTICAL_TOUR_TIER = 'paced_tour';
const SIMPLE_REEL_MAX = 9;
const ADDON_MAX_QTY = 99;
// Under this the order page asks for the size again (OrderV2's size gate).
const MIN_SQFT = 100;

// The website's add-ons, keyed by their link id, with the RotoGears add-on each
// one is sold as and the gates from addOnsV2 / availableAddOns.
const SITE_ADDONS = {
  ao_vtwilight: { rg: 'ao_vtwilight', perImage: true, appliesTo: ['photo'], includedByFeatures: { photo: ['twilight'] } },
  ao_vstaging: { rg: 'ao_vstaging', perImage: true, appliesTo: ['photo'], excludedByVariants: { photo: ['aerial_photos'] } },
  ao_sitelines: { rg: 'ao_proplines', perImage: true, appliesTo: ['photo', 'lot'], requiresFeature: 'aerial' },
  ao_beacons: { rg: 'ao_beacons', perImage: true, appliesTo: ['photo', 'lot'], requiresFeature: 'aerial' },
  ao_fp3d: { rg: 'ao_fp3d', appliesTo: ['tour'], requiresProduct: 'tour_fp' },
  ao_fpsameday: { rg: 'ao_fpsameday', appliesTo: ['tour'], requiresProduct: 'tour_fp', maxTier: 3 },
  ao_fpansi: { rg: 'ao_fpansi', appliesTo: ['tour'], requiresProduct: 'tour_fp' },
  ao_fpadu: { rg: 'ao_fpadu', appliesTo: ['tour'], requiresProduct: 'tour_fp' },
  ao_website: { rg: 'ao_website', appliesTo: ['photo', 'film', 'flythrough', 'reel', 'tour'] },
};

// ── Website pick → RotoGears New-list id ────────────────────────────────────

const PHOTO_PRODUCT = { base: 'photo_ie', flash_photos: 'photo_flash', aerial_photos: 'aerial_home' };
// The Aerial photography option on a ground tier is the "+ Aerial Photos" product.
const PHOTO_WITH_AERIAL = { photo_ie: 'photo_iea', photo_flash: 'photo_flash_a' };
const FILM_PRODUCT = { base: 'video_mls', listing_film: 'video_film', estate_film: 'film_sig' };
const FLYTHROUGH_PRODUCT = { base: 'drone_mls', paced_tour: 'drone_reel', story_tour: 'drone_story' };
const REEL_PRODUCT = { base: 'video_reel', dynamic_reel: 'reel_dynamic', story_reel: 'reel_story' };
const TOUR_PRODUCT = { tour_fp: 'fp_2d', tour_matterport: 'matterport', tour_matterport_fp: 'matterport_fp', tour_zillow: 'zillow' };
const LOT_PRODUCT = { lot_photo: 'aerial_lot_photo', lot_video: 'aerial_lot_video', lot_bundle: 'aerial_lot_bundle' };
// Options sold as RotoGears add-ons. The photo service's Aerial and True
// twilight options are products instead (see mapService).
const FEATURE_ADDON = {
  photo: { ig_carousel: 'ao_ig_carousel' },
  film: { fpv: 'ao_lv_fpv', teaser_reel: 'ao_lv_teaser', speed_ramps: 'ao_lv_ramps', agent_on_camera: 'ao_lv_agent', timelapse: 'ao_timelapse' },
  flythrough: { teaser_reel: 'ao_tour_teaser' },
  reel: { agent_on_camera: 'ao_reel_agent', custom_captions: 'ao_reel_captions', timelapse: 'ao_reel_timelapse', speed_ramps: 'ao_reel_ramps' },
};
// What "each" counts when the website charges an option per copy.
const COPY_NOUN = { photo: ['photo set', 'photo sets'], film: ['video', 'videos'], flythrough: ['RotoTour', 'RotoTours'], reel: ['reel', 'reels'] };

// Link letter → website service (urlStateV2 SERVICE_KEYS).
const SERVICE_KEYS = { p: 'photo', h: 'flythrough', m: 'film', r: 'reel', q: 'quickreel', u: 'tour', l: 'lot' };
const SERVICE_LABEL = {
  photo: 'Photos', film: 'Listing Video', flythrough: 'RotoTour', reel: 'Reel',
  quickreel: 'Simple Reel', tour: 'Floor plan or 3D tour', lot: 'Land',
};
// Any of these makes a query string an order (cat, path, rc and sb carry no
// order on their own).
const ORDER_PARAMS = ['sqft', 't', 'pkg', 'ao', ...Object.keys(SERVICE_KEYS)];

const own = (obj, key) => Object.keys(obj).includes(key);
const uniq = (ids) => [...new Set(ids)];
const money = (n) => `$${Math.round(n).toLocaleString('en-US')}`;
const sqftText = (n) => Number(n).toLocaleString('en-US');

// The order page's size band a size falls in (sqftBands): 0 to 999, then 500
// sq ft steps to 9,999, then 10,000+.
function siteBandLabel(n) {
  if (n >= 10000) return '10,000+';
  if (n < 1000) return '0 to 999';
  const lo = Math.floor(n / 500) * 500;
  return `${sqftText(lo)} to ${sqftText(lo + 499)}`;
}

// ── Reading the pasted text ─────────────────────────────────────────────────

const SITE_HOSTS = ['rotoflight.com', 'www.rotoflight.com', 'rotoflightmedia.com', 'www.rotoflightmedia.com', 'localhost:5175'];
const ORDER_PATH = '/real-estate/order';

/**
 * The query string of a website order link, or null. Takes the full link (from
 * rotoflight.com, rotoflightmedia.com or the dev site on localhost:5175, with
 * or without https://), a bare query string ("?sqft=3000&p=_" or
 * "sqft=3000&p=_"), or the website's copied quote text, whose last line is
 * the link.
 */
export function siteOrderQuery(text) {
  // Punctuation after the link ("...&p=_.") ends the sentence, not the order:
  // nothing the order page writes ends in one of these.
  const unpunctuate = (v) => v.replace(/[.,!?]+$/, '');
  const s = unpunctuate(String(text ?? '').trim());
  if (!s) return null;
  // The host must start the text or follow a space or bracket, so a lookalike
  // (xrotoflight.com) never passes as the real one.
  const found = unpunctuate(s.match(/(?:^|[\s<("'])((?:https?:\/\/)?(?:www\.)?(?:rotoflight(?:media)?\.com|localhost:\d+)\/[^\s<>"')]*)/i)?.[1] || '');
  if (found) {
    let url;
    try {
      url = new URL(/^https?:\/\//i.test(found) ? found : `https://${found}`);
    } catch {
      return null;
    }
    if (!SITE_HOSTS.includes(url.host.toLowerCase())) return null;
    if (url.pathname.replace(/\/+$/, '') !== ORDER_PATH) return null;
    return url.search;
  }
  if (/\s/.test(s) || !/^\??[a-z]+=/i.test(s)) return null;
  return s.startsWith('?') ? s : `?${s}`;
}

// ── Decoding (urlStateV2.decodeSelection / readConfigFromURLV2) ──────────────

function knownFeatures(serviceId, ids, unknown) {
  const svc = SITE_SERVICES[serviceId];
  return uniq(ids.filter((id) => {
    if (!id || id === '_') return false;
    if (svc.features.includes(id)) return true;
    unknown.push(`${svc.label} option "${id}"`);
    return false;
  }));
}

function decodeMore(serviceId, part, ownKey, unknown) {
  const svc = SITE_SERVICES[serviceId];
  if (!svc.multiLine) return null;
  const more = {};
  part.slice(2).split(',').forEach((pair) => {
    const [k, nStr] = pair.split('.');
    const n = Math.min(parseInt(nStr, 10) || 0, 9);
    if (k === ownKey || !(n > 0)) return;
    if (k === 'base' || own(svc.variants, k)) more[k] = n;
    else unknown.push(`${svc.label} tier "${k}"`);
  });
  return Object.keys(more).length ? more : null;
}

function decodeSelection(serviceId, raw, unknown) {
  if (!raw) return null;
  const label = SERVICE_LABEL[serviceId];
  if (serviceId === 'quickreel') {
    const n = parseInt(raw, 10);
    return n > 0 ? { qty: Math.min(n, SIMPLE_REEL_MAX) } : null;
  }
  if (serviceId === 'tour' || serviceId === 'lot') {
    const list = serviceId === 'tour' ? TOUR_PRODUCT : LOT_PRODUCT;
    if (own(list, raw)) return { product: raw };
    unknown.push(`${label} "${raw}"`);
    return null;
  }
  const svc = SITE_SERVICES[serviceId];
  const checkFormat = (f) => {
    if (!SITE_FORMATS.includes(f)) unknown.push(`${label} format "${f}"`);
    return f;
  };
  if (raw.startsWith('v:')) {
    const [head, ...extras] = raw.split(';');
    const variantId = head.slice(2);
    if (!own(svc.variants, variantId)) {
      unknown.push(`${label} tier "${variantId}"`);
      return null;
    }
    const sel = { variantId, features: [...(svc.variants[variantId].includes || [])] };
    const listed = extras.filter((p) => !p.startsWith('f:') && !p.startsWith('q:') && !p.startsWith('x:'));
    if (listed.length) sel.features = knownFeatures(serviceId, listed.join(',').split(','), unknown);
    const fmt = extras.find((p) => p.startsWith('f:'));
    if (fmt) sel.formatId = checkFormat(fmt.slice(2));
    const q = parseInt((extras.find((p) => p.startsWith('q:')) || '').slice(2), 10);
    if (q > 1) sel.qty = Math.min(q, 9);
    const x = extras.find((p) => p.startsWith('x:'));
    const more = x && decodeMore(serviceId, x, variantId, unknown);
    if (more) sel.more = more;
    if (serviceId === 'flythrough' && !sel.formatId) sel.formatId = 'horizontal';
    return sel;
  }
  const sel = { features: [] };
  let listed = [];
  raw.split(';').forEach((part) => {
    if (part === '_' || part === '') return;
    if (part.startsWith('f:')) sel.formatId = checkFormat(part.slice(2));
    else if (part.startsWith('q:')) {
      const n = parseInt(part.slice(2), 10);
      if (n > 1) sel.qty = Math.min(n, 9);
    } else if (part.startsWith('x:')) {
      const more = decodeMore(serviceId, part, 'base', unknown);
      if (more) sel.more = more;
    } else listed = part.split(',');
  });
  sel.features = knownFeatures(serviceId, listed, unknown);
  if (serviceId === 'flythrough' && !sel.formatId) sel.formatId = 'horizontal';
  return sel;
}

/**
 * A pasted website order link, decoded as the order page reads it. null when
 * the text isn't an order link. `unknown` lists every id the link carries that
 * this file doesn't know (the website would drop most of them too, but a new
 * option the website charges for shows up here first).
 */
export function parseSiteOrderLink(text) {
  const query = siteOrderQuery(text);
  if (query == null) return null;
  const params = new URLSearchParams(query);
  if (!ORDER_PARAMS.some((p) => params.has(p))) return null;
  const unknown = [];
  const selections = {};
  for (const [key, serviceId] of Object.entries(SERVICE_KEYS)) {
    const sel = decodeSelection(serviceId, params.get(key), unknown);
    if (sel) selections[serviceId] = sel;
  }
  const addOns = {};
  (params.get('ao') || '').split(',').forEach((token) => {
    const [short, qtyStr] = token.split(':');
    if (!short) return;
    const id = `ao_${short}`;
    if (!own(SITE_ADDONS, id)) {
      unknown.push(`add-on "${short}"`);
      return;
    }
    addOns[id] = SITE_ADDONS[id].perImage
      ? Math.min(ADDON_MAX_QTY, Math.max(1, parseInt(qtyStr, 10) || 1))
      : 1;
  });
  // An old link's band was the middle of its 1,000 sq ft tier; it reopens in
  // the tier's lower half, at the price the buyer was quoted.
  let sqft = params.get('sqft') || '';
  const sqftIsBand = params.get('sb') === '1';
  if (sqftIsBand) {
    const n = Number(sqft);
    if (n >= 1500 && n % 1000 === 500) sqft = String(n - 250);
  }
  // The quote stores whole square feet; a size that isn't one prices nothing
  // on the website either, so it reads as no size. Nor does one under the
  // order page's minimum, which asks for the size again before pricing.
  const sqftUnderMin = Number(sqft) > 0 && Number(sqft) < MIN_SQFT ? sqft : '';
  sqft = Number(sqft) >= MIN_SQFT ? String(Math.floor(Number(sqft))) : '';
  const t = params.get('t');
  return {
    sqft,
    sqftIsBand,
    sqftUnderMin,
    land: t === 'land',
    selections,
    addOns,
    packageId: params.get('pkg') || null,
    unknown,
  };
}

// ── What the website charges for a restored link ────────────────────────────

const variantOf = (serviceId, variantId) =>
  (variantId && SITE_SERVICES[serviceId] && own(SITE_SERVICES[serviceId].variants, variantId)
    ? SITE_SERVICES[serviceId].variants[variantId]
    : null);

// selectionLines: the selection's own tier with its count, then the other reel
// tiers taken beside it.
function siteLines(serviceId, sel) {
  const primaryKey = sel.variantId || 'base';
  const lines = [{ key: primaryKey, variantId: sel.variantId, qty: sel.qty || 1, primary: true }];
  if (!SITE_SERVICES[serviceId].multiLine) return lines;
  for (const [key, qty] of Object.entries(sel.more || {})) {
    if (key === primaryKey || !(qty > 0)) continue;
    if (key !== 'base' && !variantOf(serviceId, key)) continue;
    lines.push({ key, variantId: key === 'base' ? undefined : key, qty });
  }
  return lines;
}

// getFeaturesAfterVariant: the options a selection carries onto another tier,
// that tier's own work ticked, the paid options kept, what the old tier stood
// in for kept as the option itself.
function featuresAfterVariant(serviceId, sel, variantId) {
  const prev = variantOf(serviceId, sel.variantId);
  const prevFree = [...(prev?.includes || []), ...(prev?.provides || [])];
  const paid = [...sel.features.filter((f) => !prevFree.includes(f)), ...(prev?.provides || [])];
  const v = variantOf(serviceId, variantId);
  const free = [...(v?.includes || []), ...(v?.provides || [])];
  return uniq([...(v?.includes || []), ...paid.filter((f) => !free.includes(f) && SITE_SERVICES[serviceId].features.includes(f))]);
}

// featuresWithRequirements: none the tier refuses, none whose need is missing.
function withRequirements(serviceId, ids, variant) {
  const requires = SITE_SERVICES[serviceId].requires || {};
  const excluded = variant?.excludes || [];
  const list = ids.filter((id) => !excluded.includes(id));
  const have = new Set([...list, ...(variant?.includes || [])]);
  return list.filter((id) => !requires[id] || have.has(requires[id]));
}

// featuresForFormat: none its delivery format can't take.
function forFormat(serviceId, ids, formatId) {
  if (!formatId) return ids;
  const formats = SITE_SERVICES[serviceId].formats || {};
  return ids.filter((id) => !formats[id] || formats[id].includes(formatId));
}

// lineFeatureIds + getServicePrice's filters: the options one line prices with.
function lineFeatures(serviceId, sel, line) {
  const lines = siteLines(serviceId, sel);
  const v = variantOf(serviceId, line.variantId);
  const ids = lines.length > 1
    ? withRequirements(serviceId, uniq([...sel.features, ...(v?.includes || [])]), v)
    : withRequirements(serviceId, sel.features, variantOf(serviceId, sel.variantId));
  return forFormat(serviceId, ids, line.primary ? sel.formatId : undefined);
}

// The order page's cleanup after a restore (useConfiguratorV2): an option goes
// when what it needs goes (featureRequirementMet) or no reel/format takes it
// (featureOffered). Repeated until nothing changes, as the effect re-runs.
function settleFeatures(serviceId, sel, dropped) {
  const svc = SITE_SERVICES[serviceId];
  const requires = svc.requires || {};
  const formats = svc.formats || {};
  let features = sel.features;
  for (;;) {
    const cur = { ...sel, features };
    const lines = siteLines(serviceId, cur);
    const kept = features.filter((f) => {
      if (formats[f] && !formats[f].includes(cur.formatId || 'horizontal')) return false;
      if (!lines.some((l) => !(variantOf(serviceId, l.variantId)?.excludes || []).includes(f))) return false;
      const need = requires[f];
      if (!need) return true;
      if (lines.length <= 1) return features.includes(need);
      return lines.some((l) => {
        const v = variantOf(serviceId, l.variantId);
        if ((v?.excludes || []).includes(f)) return false;
        return features.includes(need) || (v?.includes || []).includes(need);
      });
    });
    if (kept.length === features.length) break;
    features.filter((f) => !kept.includes(f)).forEach((f) => { dropped.push({ serviceId, feature: f }); });
    features = kept;
  }
  return { ...sel, features };
}

// hasShootForSimpleReel(selections, { withReel: true }).
function simpleReelHasShoot(selections) {
  if (selections.film || selections.flythrough || selections.reel) return true;
  if (selections.lot && selections.lot.product !== 'lot_photo') return true;
  return !!selections.photo && selections.photo.variantId !== 'aerial_photos';
}

// availableAddOns, minus the locked ones: whether the website charges an add-on.
// Returns null when it does, else why not.
function siteAddonRefusal(id, selections, tierIndex) {
  const ao = SITE_ADDONS[id];
  if (!ao.appliesTo.some((s) => selections[s])) {
    return `on the website it goes with ${ao.appliesTo.map((s) => SERVICE_LABEL[s]).join(' or ')}, and the order has none`;
  }
  if (ao.maxTier != null && tierIndex > ao.maxTier) return `on the website it stops at ${((ao.maxTier + 1) * 1000 - 1).toLocaleString('en-US')} sq ft`;
  if (ao.requiresProduct && !ao.appliesTo.some((s) => selections[s]?.product === ao.requiresProduct)) return 'on the website it needs the 2D Floor Plan';
  if (Object.entries(ao.excludedByVariants || {}).some(([s, ids]) => ids.includes(selections[s]?.variantId))) {
    return 'the website doesn\'t offer it on Aerial Photos';
  }
  if (ao.requiresFeature) {
    const met = ao.appliesTo.some((s) => {
      const sel = selections[s];
      if (!sel) return false;
      if (!SITE_SERVICES[s]?.features.includes(ao.requiresFeature)) return s !== 'lot' || sel.product !== 'lot_video';
      if ((variantOf(s, sel.variantId)?.provides || []).includes(ao.requiresFeature)) return true;
      return (sel.features || []).includes(ao.requiresFeature);
    });
    if (!met) return 'on the website it needs aerial photos';
  }
  const included = Object.entries(ao.includedByFeatures || {})
    .some(([s, ids]) => ids.some((f) => (selections[s]?.features || []).includes(f)));
  if (included) return 'it comes with the true twilight session on the website';
  return null;
}

// ── Website order → RotoGears quote ─────────────────────────────────────────

const rgName = (id) =>
  rgFindProduct(id, RG_CURRENT_VERSION)?.product.name || rgFindAddon(id, RG_CURRENT_VERSION)?.name || id;

/**
 * What a decoded website order is on the RotoGears New list:
 *   { sqft, selectedProducts, productQuantities, selectedAddons,
 *     addonQuantities, notes }
 * in the builder state's own shapes (IMPORT_SITE_SELECTION takes it as is).
 * `sqft` is '' when the link carries none; `opts.sqft` (the quote's) then
 * stands in for the size rules.
 *
 * `notes` is everything that didn't come across exactly, each
 *   { kind: 'unmatched' | 'differs' | 'info', text, group, amount }
 * where `amount` is what the website charges beyond this quote (negative when
 * the quote charges more, null when it can't be known) and `group` the website
 * card it belongs to.
 */
export function siteOrderToQuote(order, opts = {}) {
  const notes = [];
  const note = (kind, text, group, amount = 0) => { notes.push({ kind, text, group, amount }); };
  const sqftNum = parseInt(order.sqft || opts.sqft, 10) || 0;
  const tierIndex = rgTierIndex(sqftNum);

  order.unknown.forEach((u) => { note('unmatched', `Not recognised: ${u}. Check the website order and add it by hand.`, null, null); });
  if (order.packageId) {
    note('unmatched', `The link has a package (${order.packageId}). Packages aren't imported, so every service is priced on its own.`, null, null);
  }

  // OrderV2's hydration: a land order restores only the land product, a
  // building order never carries it.
  const selections = {};
  for (const [serviceId, sel] of Object.entries(order.selections)) {
    if (order.land ? serviceId !== 'lot' : serviceId === 'lot') {
      note('info', `${SERVICE_LABEL[serviceId]}: ${order.land ? 'a land order on the website is the land product only' : 'land products aren\'t sold on a building order'}, so it wasn't imported.`, serviceId);
      continue;
    }
    selections[serviceId] = sel;
  }
  // BuildPathwayV2: a vertical cut has no Real Time Tour, so a vertical RotoTour
  // with no tier lands on the first one however it got there, a link included
  // (Cyprian, 2026-09-30), carrying its options as setVariant does.
  const fly = selections.flythrough;
  if (fly && fly.formatId === 'vertical' && !fly.variantId) {
    selections.flythrough = { ...fly, variantId: VERTICAL_TOUR_TIER, features: featuresAfterVariant('flythrough', fly, VERTICAL_TOUR_TIER) };
    note('info', 'RotoTour: the link has a vertical Real Time Tour, which the website doesn\'t sell (the Real Time Tour is horizontal only). The website prices it as the Paced Tour, and so does this quote.', 'flythrough');
  }
  const dropped = [];
  for (const serviceId of Object.keys(SITE_SERVICES)) {
    if (selections[serviceId]) selections[serviceId] = settleFeatures(serviceId, selections[serviceId], dropped);
  }
  dropped.forEach(({ serviceId, feature }) => {
    const aoId = FEATURE_ADDON[serviceId]?.[feature];
    note('info', `${aoId ? rgName(aoId) : feature}: the website doesn't sell it with this ${SERVICE_LABEL[serviceId]}, so it wasn't imported.`, serviceId);
  });
  if (selections.quickreel && !simpleReelHasShoot(selections)) {
    note('info', 'Simple Reel: it rides along with another shoot on the website, and this order has none, so it wasn\'t imported.', 'quickreel');
    delete selections.quickreel;
  }

  const products = new Map(); // RotoGears product id → qty
  const addonCharges = new Map(); // add-on id → { charges, group, serviceId }
  const addonQuantities = {};
  const addProduct = (id, qty) => products.set(id, (products.get(id) || 0) + qty);
  const chargeAddon = (id, times, serviceId) => {
    const cur = addonCharges.get(id) || { charges: 0, group: serviceId, serviceId };
    cur.charges += times;
    addonCharges.set(id, cur);
  };

  // A v2 service: each line (one, or several reel tiers) is a product, its
  // paid options add-ons, both times the line's count.
  for (const serviceId of Object.keys(SITE_SERVICES)) {
    const sel = selections[serviceId];
    if (!sel) continue;
    for (const line of siteLines(serviceId, sel)) {
      const feats = lineFeatures(serviceId, sel, line);
      const v = variantOf(serviceId, line.variantId);
      const free = [...(v?.includes || []), ...(v?.provides || [])];
      const paid = feats.filter((f) => !free.includes(f));
      const key = line.variantId || 'base';
      if (serviceId === 'photo') {
        const ground = PHOTO_PRODUCT[key];
        addProduct(paid.includes('aerial') && PHOTO_WITH_AERIAL[ground] ? PHOTO_WITH_AERIAL[ground] : ground, line.qty);
        if (paid.includes('twilight')) addProduct('photo_tw', line.qty);
      } else if (serviceId === 'film') {
        addProduct(FILM_PRODUCT[key], line.qty);
      } else if (serviceId === 'flythrough') {
        addProduct(FLYTHROUGH_PRODUCT[key], line.qty);
        if (sel.formatId === 'both') chargeAddon('ao_tour_both', line.qty, serviceId);
      } else if (serviceId === 'reel') {
        addProduct(REEL_PRODUCT[key], line.qty);
      }
      paid.forEach((f) => {
        const aoId = FEATURE_ADDON[serviceId][f];
        if (aoId) chargeAddon(aoId, line.qty, serviceId);
      });
    }
  }

  // The Simple Reel: the first at its size's price, each after it the flat rate.
  if (selections.quickreel) {
    addProduct('reel_simple', 1);
    const extra = selections.quickreel.qty - 1;
    if (extra > 0) {
      chargeAddon('ao_simple_extra', 1, 'quickreel');
      addonQuantities.ao_simple_extra = extra;
    }
  }
  if (selections.tour) addProduct(TOUR_PRODUCT[selections.tour.product], 1);
  if (selections.lot) addProduct(LOT_PRODUCT[selections.lot.product], 1);

  // The website's own add-ons, where it charges them.
  for (const [id, qty] of Object.entries(order.addOns)) {
    const ao = SITE_ADDONS[id];
    const refusal = siteAddonRefusal(id, selections, tierIndex);
    if (refusal) {
      note('info', `${rgName(ao.rg)}: ${refusal}, so it wasn't charged or imported.`, id);
      continue;
    }
    const rgAddon = rgFindAddon(ao.rg, RG_CURRENT_VERSION);
    if (rgAddon && rgAddonPrice(rgAddon, sqftNum) == null) {
      note('info', `${rgAddon.name}: quoted, not sold, at this size, so it wasn't imported.`, id);
      continue;
    }
    chargeAddon(ao.rg, 1, id);
    if (ao.perImage) addonQuantities[ao.rg] = qty;
  }

  const selectedProducts = {};
  const productQuantities = {};
  for (const [id, qty] of products) {
    selectedProducts[id] = RG_CURRENT_VERSION;
    if (qty > 1) productQuantities[id] = qty;
  }
  const selectedAddons = {};
  for (const id of addonCharges.keys()) selectedAddons[id] = true;

  // Anything RotoGears won't bill beside these picks comes off, said once. One
  // coming off can take another with it (captions need the agent), so again
  // until nothing changes.
  let billable;
  let ctx;
  for (;;) {
    billable = rgBillableAddonIds(selectedProducts, selectedAddons, sqftNum, RG_CURRENT_VERSION);
    ctx = rgPriceContext(Object.keys(selectedProducts), billable);
    const off = [...addonCharges.keys()].filter((id) => !billable.has(id));
    if (!off.length) break;
    for (const id of off) {
      const addon = rgFindAddon(id, RG_CURRENT_VERSION);
      const qty = addon.perImage ? (addonQuantities[id] || 1) : 1;
      const charged = (rgAddonPrice(addon, sqftNum, ctx) ?? 0) * qty;
      // Its category isn't on the quote (an Instagram carousel beside Aerial
      // Photos alone), or every pick there refuses it.
      const beside = Object.keys(selectedProducts).filter((pid) => addon.applies.includes(rgFindProduct(pid, RG_CURRENT_VERSION).category.id));
      const why = beside.length
        ? `RotoGears doesn't sell it with ${beside.map(rgName).join(' or ')}`
        : `RotoGears sells it only beside a service on its ${addon.applies.map((c) => SVC_DB_NEW.categories.find((x) => x.id === c)?.name).join(' or ')} tab, and none of the imported picks is there`;
      note('unmatched', `${addon.name} (${money(charged)} on the website): ${why}, so it was left off. Add it by hand if it belongs.`, addonCharges.get(id).group, charged);
      delete selectedAddons[id];
      delete addonQuantities[id];
      addonCharges.delete(id);
    }
  }

  // RotoGears bills an add-on once per quote; the website charges an option on
  // every reel (or copy) that doesn't already include it.
  for (const [id, { charges, group, serviceId }] of addonCharges) {
    const addon = rgFindAddon(id, RG_CURRENT_VERSION);
    if (addon.perImage || charges <= 1) continue;
    const price = rgAddonPrice(addon, sqftNum, ctx) ?? 0;
    const [one, many] = COPY_NOUN[serviceId] || ['copy', 'copies'];
    note('differs', `${addon.name}: the website charges it on each of ${charges} ${charges === 1 ? one : many}, RotoGears bills it once (${money(price * (charges - 1))} less here).`, group, price * (charges - 1));
  }
  // The reel's Timelapse is cheaper beside an Estate Film in RotoGears even when
  // the film's own Timelapse was unticked; the website then charges it in full.
  const film = selections.film;
  const reelTimelapse = addonCharges.get('ao_reel_timelapse');
  if (reelTimelapse && film?.variantId === 'estate_film' && !film.features.includes('timelapse')) {
    const addon = rgFindAddon('ao_reel_timelapse', RG_CURRENT_VERSION);
    const here = rgAddonPrice(addon, sqftNum, ctx) ?? 0;
    const gap = (addon.price - here) * reelTimelapse.charges;
    if (gap) {
      note('differs', `${addon.name}: ${money(here)} here beside the Estate Film, ${money(addon.price)} on the website because the film's timelapse was unticked (${money(gap)} less here).`, 'reel', gap);
    }
  }

  const sized = [...products.keys()].some((id) => rgIsTiered(rgFindProduct(id, RG_CURRENT_VERSION)?.product));
  if (!order.sqft && sized) {
    const why = order.sqftUnderMin
      ? `The link's ${sqftText(order.sqftUnderMin)} sq ft is under the website's ${MIN_SQFT} sq ft minimum (the website asks for the size again)`
      : 'The link has no square footage';
    note('info', opts.sqft
      ? `${why}, so sized services are priced at the quote's ${sqftText(opts.sqft)} sq ft.`
      : `${why}, so sized services are priced at the smallest size until one is entered.`, null, null);
  } else if (order.sqftIsBand && order.sqft) {
    note('info', `The buyer picked a size range (${siteBandLabel(Number(order.sqft))} sq ft), not an exact size. The quote uses ${sqftText(order.sqft)}, as the website did.`, null);
  }

  return { sqft: order.sqft, selectedProducts, productQuantities, selectedAddons, addonQuantities, notes };
}

/**
 * A quote selection's Real Estate total on the New list, priced as the line
 * rebuild prices it: each product times its count, each add-on where it bills.
 * Quoted (unpriced) items count $0.
 */
export function rgSelectionTotal({ sqft, selectedProducts = {}, productQuantities = {}, selectedAddons = {}, addonQuantities = {} }) {
  const n = parseInt(sqft, 10) || 0;
  const billable = rgBillableAddonIds(selectedProducts, selectedAddons, n, RG_CURRENT_VERSION);
  const ctx = rgPriceContext(Object.keys(selectedProducts), billable);
  let total = 0;
  for (const id of Object.keys(selectedProducts)) {
    const found = rgFindProduct(id, RG_CURRENT_VERSION);
    if (!found) continue;
    total += (rgGetPrice(found.product, n, RG_CURRENT_VERSION, ctx).price ?? 0) * (productQuantities[id] || 1);
  }
  for (const id of billable) {
    const addon = rgFindAddon(id, RG_CURRENT_VERSION);
    total += (rgAddonPrice(addon, n, ctx) ?? 0) * (addon.perImage ? (addonQuantities[id] || 1) : 1);
  }
  return total;
}
